import React, { useCallback, useMemo, useState } from 'react';
import {
  createLocaleContext,
  LocaleContext,
  Texts,
} from '../../shared/context/LocaleContext';
import { MultiValuesNode } from '../../types/chartDataTypes';
import {
  findEntryWithName,
  getLeafNodes,
  mapNode,
} from '../../shared/helper/chartDataHelper';
import { prepareData } from './BalanceSheet.helper';
import {
  Container,
  ChartWrapper,
  HeaderContainer,
  TableContainer,
} from './BalanceSheet.styles';
import CustomBarChart from './BarChart/BarChart';
import DataTable from '../../shared/components/DataTable/DataTable';
import TableAndChart from '../../shared/components/TableAndChart/TableAndChart';
import DataTableModal from '../../shared/components/DataTableModal/DataTableModal';
import Header from '../../shared/components/Header/Header';
import { getUrlForKey } from '../../shared/helper/links';

export type BalanceSheetProps = {
  data: MultiValuesNode[],
  dataColumns: string[],
  unitInfo: string,
  notesLabel: string,
  notesUrlFallback: string,
  notesUrlMapping: {
    [note: string]: string;
  };
  language?: string,
  texts: Texts,
};

const BalanceSheet = ({
  data,
  dataColumns,
  unitInfo,
  notesLabel,
  notesUrlFallback,
  notesUrlMapping,
  language = 'en',
  texts,
}: BalanceSheetProps): React.ReactElement => {
  const [modalContent, setModalContent] = useState(null as MultiValuesNode);
  const [modalColumn, setModalColumn] = useState(null as string);
  const [markedEntry, setMarkedEntry] = useState(null as string);

  const localeContext = createLocaleContext(language, texts);

  const chartData = useMemo(() => prepareData(data), [data]);

  const highlightEntry = useCallback((entryTitle: string): void => {
    if (entryTitle !== chartData.liabilitiesAndReserves.totalLabel
      && entryTitle !== chartData.allTableData.entries[0].totalLabel) {
      setMarkedEntry(entryTitle);
    }
  }, [chartData, setMarkedEntry]);

  const onBarClick = useCallback((barName: string, columnName: string): void => {
    const completeEntry = findEntryWithName(chartData.barChart, barName);
    if (completeEntry) {
      const mappedNode = mapNode(completeEntry, (entry: MultiValuesNode, result: MultiValuesNode) => {
        result.values = {
          [columnName]: entry.values[columnName],
          [notesLabel]: (entry as any).note,
        };
      });
      setModalColumn(columnName);
      setModalContent({
        name: mappedNode.name,
        totalLabel: mappedNode.totalLabel,
        values: mappedNode.values,
        entries: getLeafNodes(mappedNode),
      });
    }
  }, [chartData, notesLabel, setModalColumn, setModalContent]);

  const entryShouldOpenModal = useCallback((searchLabel: string): boolean => {
    const completeEntry = findEntryWithName(chartData.barChart, searchLabel);
    return completeEntry && completeEntry.entries?.length && !completeEntry.entries[0].entries?.length;
  }, [chartData]);

  const entryClickTargetBuilder = useCallback(
    (entry: MultiValuesNode, columnName: string): (() => void) => {
      const searchLabel = entry.totalLabel || entry.name;
      if (!entryShouldOpenModal(searchLabel)) {
        return undefined;
      }
      return (
        () => {
          onBarClick(searchLabel, columnName);
        }
      );
    }, [entryShouldOpenModal, onBarClick],
  );

  const layerEntryClickTargetBuilder = useCallback((entry: MultiValuesNode, column: string) => {
    if (column === notesLabel) {
      return getUrlForKey(entry.values[notesLabel], notesUrlMapping, notesUrlFallback);
    }
    return undefined;
  }, [notesLabel, notesUrlMapping, notesUrlFallback]);

  return (
    <LocaleContext.Provider value={localeContext}>
      <Container>
        <TableAndChart>
          <TableContainer>
            <DataTable
              data={chartData.assets}
              columnKeys={dataColumns}
              headline={chartData.assets.entries[0].name}
              note={unitInfo}
              markEntryWithName={markedEntry}
              minRenderLevel={1}
              onLineHover={highlightEntry}
              entryClickTargetBuilder={entryClickTargetBuilder}
            />
            <DataTable
              data={chartData.liabilitiesAndReserves}
              columnKeys={dataColumns}
              headline={chartData.liabilitiesAndReserves.name}
              note={unitInfo}
              markEntryWithName={markedEntry}
              minRenderLevel={1}
              onLineHover={highlightEntry}
              entryClickTargetBuilder={entryClickTargetBuilder}
            />
          </TableContainer>

          <ChartWrapper>
            <HeaderContainer>
              {dataColumns.map((column) => (
                <Header key={column} headline={column} />
              ))}
            </HeaderContainer>
            <CustomBarChart
              data={chartData.barChart}
              keys={dataColumns}
              onDataHover={highlightEntry}
              onDataClick={onBarClick}
              highlightStack={markedEntry}
              hideLabel
            />
          </ChartWrapper>
        </TableAndChart>

        <DataTableModal
          dataNode={modalContent}
          dataColumns={[notesLabel, modalColumn]}
          columnLabels={{ [modalColumn]: unitInfo }}
          headlinePrefix={modalColumn}
          onRequestClose={() => setModalContent(null)}
          entryClickTargetBuilder={layerEntryClickTargetBuilder}
        />
      </Container>
    </LocaleContext.Provider>
  );
};

export default BalanceSheet;
